// Educational content — sourced from client-provided reference document.
const STATS = [
  ["$9.6T / day", "GLOBAL FX TURNOVER"],
  ["$150T+", "GLOBAL EQUITY MARKET CAP"],
  ["~$2.5T", "TOTAL CRYPTO MARKET CAP"],
  ["$29T", "ABOVE-GROUND GOLD VALUE"],
];

const LEV_TABLE = [
  ["FX", "1 : 5000", "Applies across major, minor and exotic pairs"],
  ["Metals", "1 : 2000", "Gold, silver and other precious metals"],
  ["Energy", "1 : 2000", "Crude oil, natural gas and related energy products"],
  ["Indices", "1 : 200", "Major global benchmark indices"],
  ["Crypto", "1 : 500", "BTC, ETH and other listed digital assets"],
  ["Equities / Single Stocks", "Confirm with desk", "Not included in the published leverage schedule — check current terms"],
];

const Section = ({ n, title, sub, children }) => (
  <section className="border-t border-border pt-12">
    <div className="kicker mb-2">{n}</div>
    <h2 className="text-3xl md:text-4xl mb-2">{title}</h2>
    <p className="text-sm text-muted-foreground mb-6 italic">{sub}</p>
    <div className="space-y-4 text-sm text-muted-foreground leading-relaxed">{children}</div>
  </section>
);

const Lingo = ({ items }) => (
  <div>
    <div className="kicker mb-2">Key Lingo</div>
    <ul className="space-y-2 text-sm">
      {items.map(([term, def]) => (
        <li key={term}><span className="text-foreground">{term}:</span> <span className="text-muted-foreground">{def}</span></li>
      ))}
    </ul>
  </div>
);

const FactTable = ({ rows }) => (
  <table className="w-full text-sm">
    <tbody>
      {rows.map(([k, v]) => (
        <tr key={k} className="border-t border-border">
          <td className="py-2 pr-4 kicker text-[10px]">{k}</td>
          <td className="py-2 text-muted-foreground">{v}</td>
        </tr>
      ))}
    </tbody>
  </table>
);

export default function MarketsGuide() {
  return (
    <div className="container-tight py-16 max-w-5xl space-y-12">
      <div>
        <div className="kicker mb-3">Trading Desk Reference</div>
        <h1 className="text-5xl md:text-6xl mb-4 leading-[1.05]">The Five Markets We Trade</h1>
        <p className="text-muted-foreground max-w-3xl leading-relaxed">
          FX, Metals, Digital Assets, Indices & Equities — market size, terminology, contract units
          and leverage conventions, in one page per asset class.
        </p>
        <div className="grid md:grid-cols-4 gap-4 mt-10">
          {STATS.map(([v, k]) => (
            <div key={k} className="stat-tile"><div className="font-mono text-xl gold">{v}</div><div className="kicker text-[10px] mt-2">{k}</div></div>
          ))}
        </div>
      </div>

      <Section n="01" title="Foreign Exchange (FX)" sub="The world's largest, most liquid market">
        <p>FX is the exchange of one currency for another, quoted in pairs (e.g., EUR/USD). It trades nearly 24 hours a day across Sydney, Tokyo, London and New York sessions, with no single central exchange — all trading is over-the-counter (OTC) through a network of banks, brokers and electronic venues.</p>
        <div><div className="kicker mb-2">Market Size</div>
          <p>Global FX turnover reached roughly $9.6 trillion per day in April 2025 (BIS Triennial Survey) — more than all global equity and bond markets combined. The US dollar sits on one side of about 89% of all trades; the euro and yen are the next most active currencies.</p>
        </div>
        <Lingo items={[
          ["Pip", "the smallest standard price move in a pair — typically the 4th decimal (2nd for JPY pairs)."],
          ["Spread", "the gap between the bid (sell) and ask (buy) price — the broker's built-in cost."],
          ["Lot", "the standard trade size — see units below."],
          ["Base / quote currency", "in EUR/USD, EUR is the base, USD is the quote; the price shows how much quote currency buys one unit of base."],
          ["Majors / minors / exotics", "majors pair a major currency with USD; minors exclude USD; exotics include emerging-market currencies."],
          ["Carry trade", "borrowing a low-yield currency to fund a position in a higher-yield one, earning the rate differential."],
          ["Swap / rollover", "the overnight interest charge or credit for holding a position past the daily cutoff."],
        ]} />
        <FactTable rows={[
          ["Standard lot", "100,000 units of base currency"],
          ["Mini lot", "10,000 units"],
          ["Micro lot", "1,000 units"],
          ["Pip value", "≈ $10 per standard lot on most USD-quoted pairs"],
          ["Quoting convention", "bid/ask, e.g. EUR/USD 1.0842 / 1.0844. Direction is \"long\" (buy) or \"short\" (sell)."],
        ]} />
      </Section>

      <Section n="02" title="Metals" sub="Precious & industrial hedges, traded spot and futures">
        <p>Metals trading spans precious metals (gold, silver, platinum, palladium) held as stores of value, and industrial/base metals (copper, aluminum) tied to global growth. Precious metals trade nearly around the clock via spot OTC markets, futures exchanges (COMEX, LME) and physically-backed ETFs.</p>
        <div><div className="kicker mb-2">Market Size</div>
          <p>The world's above-ground gold stock — roughly 216,000 tonnes ever mined — is valued at approximately $29–31 trillion (World Gold Council, end-2025), with average daily trading volume around $227 billion. Silver's above-ground market capitalization is far smaller, at roughly $3.9 trillion, which is a core reason silver moves with greater volatility than gold for a given flow of capital.</p>
        </div>
        <Lingo items={[
          ["Spot price", "the current price for immediate delivery, the reference price most CFD/margin trades track."],
          ["XAU/USD, XAG/USD", "the FX-style tickers for gold and silver priced in US dollars."],
          ["Troy ounce", "the standard weight unit for precious metals (≈ 31.1 grams) — distinct from the everyday avoirdupois ounce."],
          ["Contango / backwardation", "when futures trade above (contango) or below (backwardation) spot — a signal of storage cost and supply tightness."],
          ["LBMA fix", "the twice-daily London benchmark price used to settle many contracts."],
        ]} />
        <FactTable rows={[
          ["Quote unit", "US dollars per troy ounce (gold, silver, platinum, palladium)"],
          ["COMEX gold future", "100 troy oz per contract"],
          ["COMEX silver future", "5,000 troy oz per contract"],
          ["CFD / margin lot", "typically standardized as 100 oz (gold) or 5,000 oz (silver) equivalent — broker-defined"],
        ]} />
      </Section>

      <Section n="03" title="Digital Assets (Crypto)" sub="24/7 markets, native volatility">
        <p>Crypto assets trade continuously, with no daily close and no centralized exchange of record — liquidity is fragmented across centralized exchanges, decentralized exchanges (DEXs) and OTC desks. Bitcoin and Ethereum anchor the market; thousands of smaller "altcoins" trade against USD, USDT or BTC.</p>
        <div><div className="kicker mb-2">Market Size</div>
          <p>Total crypto market capitalization sits at roughly $2.3–2.7 trillion (mid-2026), down from an October 2025 peak near $4.3 trillion — a reminder that this is the most volatile asset class on this page. Bitcoin represents about 57% of total market value ("dominance"); stablecoins, pegged to fiat currencies, add roughly $300 billion of on-chain liquidity.</p>
        </div>
        <Lingo items={[
          ["Market cap vs. circulating supply", "price multiplied by coins actually in circulation — not total possible supply."],
          ["Wallet / custody", "self-custody (private keys held by the client) versus exchange custody — a key risk distinction in this asset class."],
          ["Stablecoin", "a token pegged to a fiat currency (e.g., USDT, USDC), used as a trading and settlement currency."],
          ["Perpetual future (\"perp\")", "a futures contract with no expiry, kept near spot price via a periodic funding rate paid between longs and shorts."],
          ["On-chain / gas fee", "a transaction recorded directly on a blockchain, and the network fee paid to process it."],
          ["HODL", "crypto-community shorthand for holding a position through volatility rather than trading it."],
        ]} />
        <FactTable rows={[
          ["Quote unit", "US dollars (or USDT) per coin — BTC, ETH, and most majors trade in fractional units"],
          ["Contract size", "exchange-defined; commonly 1 coin per \"contract\" with fractional trading down to 0.0001 BTC or smaller"],
          ["Settlement", "spot (physical delivery to wallet/exchange) or cash-settled perpetual/derivative"],
        ]} />
      </Section>

      <Section n="04" title="Indices" sub="One trade, a whole market's worth of exposure">
        <p>A stock index tracks a defined basket of shares (e.g., the S&P 500, Nasdaq 100, FTSE 100, DAX 40, Nikkei 225) and is traded via futures, options, ETFs or index CFDs rather than by buying every underlying share. Indices are a core tool for expressing a macro view or hedging a broader equity book.</p>
        <div><div className="kicker mb-2">Market Size</div>
          <p>Indices are derivative instruments built on top of the roughly $150+ trillion global equity market (WFE/SIFMA, 2025), so their liquidity tracks the depth of their underlying constituents. Benchmark index futures (e.g., E-mini S&P 500) are among the most actively traded derivatives in the world, used by both hedgers and speculators.</p>
        </div>
        <Lingo items={[
          ["Index points", "the unit an index is quoted in — not a currency amount until multiplied by a contract's point value."],
          ["Cash index vs. futures", "the \"cash\" index is a real-time calculated value; futures are tradable contracts that expire and roll periodically."],
          ["Contract multiplier", "the dollar value of one index point for a given futures contract (e.g., $50/point on the E-mini S&P 500)."],
          ["Basis", "the difference between the futures price and the cash index, which converges as expiry approaches."],
          ["Roll", "closing an expiring futures contract and opening the next one to maintain continuous exposure."],
        ]} />
        <FactTable rows={[
          ["Quote unit", "Index points (e.g., S&P 500 at 5,850.20)"],
          ["Typical CFD unit", "$1 (or broker-defined) per point per lot"],
          ["Futures multiplier example", "E-mini S&P 500 = $50 per index point"],
        ]} />
      </Section>

      <Section n="05" title="Equities (Stocks)" sub="Direct ownership economics, traded on margin">
        <p>Single-stock trading is direct exposure to one company, priced per share on a listed exchange (NYSE, Nasdaq, LSE, and others). Unlike FX, metals and indices, equities carry company-specific risk — earnings, dividends and corporate actions all move the price independently of the broader market.</p>
        <div><div className="kicker mb-2">Market Size</div>
          <p>Global listed equity market capitalization stood at roughly $151.9 trillion at end-2025 (World Federation of Exchanges), up 18.5% year-on-year — a record high across every region. The United States alone accounts for over $77 trillion of that total.</p>
        </div>
        <Lingo items={[
          ["Bid / ask, market / limit order", "a market order fills immediately at the best available price; a limit order fills only at a specified price or better."],
          ["Dividend / ex-dividend date", "the cash payout to shareholders, and the date after which a new buyer is not entitled to the upcoming dividend."],
          ["Short selling", "selling borrowed shares to profit from a price decline, then buying them back to close."],
          ["Margin call", "a broker's demand for additional funds when a leveraged position's equity falls below a required threshold."],
          ["Market cap tiers", "large-, mid- and small-cap, referring to a company's total share value, not its price per share."],
        ]} />
        <FactTable rows={[
          ["Quote unit", "Local currency per share"],
          ["Standard trade unit", "1 share, with most brokers now supporting fractional shares"],
          ["Settlement", "T+1 for most major exchanges (delivery versus payment)"],
        ]} />
      </Section>

      <section className="border-t border-border pt-12">
        <div className="kicker mb-2">At a Glance</div>
        <h2 className="text-3xl mb-4">Leverage by Asset Class</h2>
        <p className="text-sm text-muted-foreground mb-6 leading-relaxed max-w-3xl">
          Leverage lets a trader control a larger position than their posted margin, magnifying both gains and losses.
          Maximum ratios are set per asset class and may be reduced at the account or instrument level based on
          jurisdiction, client classification and market conditions.
        </p>
        <table className="w-full text-sm">
          <thead className="text-muted-foreground text-xs uppercase tracking-widest">
            <tr><th className="text-left pb-3">Asset Class</th><th className="text-left pb-3">Maximum Leverage</th><th className="text-left pb-3">Notes</th></tr>
          </thead>
          <tbody>
            {LEV_TABLE.map(([a, l, n]) => (
              <tr key={a} className="border-t border-border">
                <td className="py-3">{a}</td>
                <td className="py-3 font-mono gold">{l}</td>
                <td className="py-3 text-xs text-muted-foreground">{n}</td>
              </tr>
            ))}
          </tbody>
        </table>
        <p className="text-xs text-muted-foreground mt-4 leading-relaxed max-w-3xl">
          Figures shown are maximum available leverage per asset class and are subject to change without notice.
          Actual leverage applied to a given position may be lower based on account tier, instrument volatility
          and applicable regulation.
        </p>
      </section>

      <div className="text-xs text-muted-foreground leading-relaxed max-w-3xl border-t border-border pt-6">
        <div className="kicker mb-2">Risk Disclosure</div>
        Trading FX, metals, energy, digital assets, indices and equities on margin carries a high level of risk and
        may not be suitable for all investors. Leverage as high as those shown above can magnify both gains and losses
        substantially, and it is possible to lose more than your initial investment in a short period of time. Market
        size and liquidity figures cited above are drawn from public third-party sources (BIS, World Federation of
        Exchanges, SIFMA, World Gold Council, CoinGecko) as of the dates indicated and will change over time. This
        document is for general information only and does not constitute investment advice or an offer to trade any
        specific instrument.
      </div>
    </div>
  );
}
